<template>
  <section class="poy-report hdg-report-theme">
    <b-card class="poy-filter-card">
      <div class="poy-heading mb-2">
        <div class="d-flex align-items-center">
          <span class="poy-heading-icon report-icon-frame mr-1"><feather-icon
            icon="LayersIcon"
            size="22"
          /></span>
          <div>
            <h4 class="mb-25">
              {{ $t('poyReport.title') }}
            </h4>
            <span class="poy-muted">{{ $t('poyReport.subtitle') }}</span>
          </div>
        </div>
        <b-button
          v-if="summaryQuery"
          variant="outline-primary"
          size="sm"
          :to="{ name: 'settlement', query: summaryQuery }"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-50"
          />{{ $t('downlineReports.backSummary') }}
        </b-button>
      </div>
      <b-form @submit.prevent="search">
        <b-row>
          <b-col
            v-if="showAgent"
            cols="12"
            md="6"
            lg="3"
          >
            <b-form-group
              :label="$t('downlineReports.scopeAgent')"
              label-for="poy-agent"
            >
              <agent-selector
                v-model="selectedAgent"
                input-id="poy-agent"
                source="downlineBets"
                :auto-select-default="false"
                :emit-self-as-option="true"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            :md="showAgent ? 6 : 12"
            :lg="showAgent ? 3 : 6"
          >
            <b-form-group
              :label="$t('ยูสเซอร์เนม')"
              label-for="poy-member"
            >
              <b-form-input
                id="poy-member"
                v-model.trim="form.username"
                :placeholder="$t('Username')"
                autocomplete="off"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-form-group
              :label="$t('poyReport.startDateTime')"
              label-for="poy-start-display"
            >
              <flat-pickr
                id="poy-start"
                v-model="form.startDate"
                :config="dateConfig"
                class="form-control"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-form-group
              :label="$t('poyReport.endDateTime')"
              label-for="poy-end-display"
            >
              <flat-pickr
                id="poy-end"
                v-model="form.endDate"
                :config="dateConfig"
                class="form-control"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-form-group
              :label="$t('poyReport.poyId')"
              label-for="poy-id"
            >
              <b-form-input
                id="poy-id"
                v-model.trim="form.poyId"
                :placeholder="$t('poyReport.poyPlaceholder')"
                autocomplete="off"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-form-group
              :label="$t('ประเภทหวย')"
              label-for="poy-lottery"
            >
              <v-select
                v-model="form.type"
                input-id="poy-lottery"
                class="poy-lottery-select"
                :options="lotteryOptions"
                label="name"
                :get-option-label="option => $tDisplay(option.name)"
                :reduce="option => String(option.id)"
                :clearable="form.type !== ''"
                :placeholder="$t('ประเภทหวย')"
                :loading="dictionariesLoading"
                @input="changeLottery"
              >
                <template #option="option">
                  <span class="poy-lottery-option">
                    <span
                      v-if="!option.isYeekee && hasFlag(option.name)"
                      class="flag-icon flag-icon-rounded poy-lottery-icon"
                      :class="getFlagIconClass(option.name)"
                      aria-hidden="true"
                    />
                    <span class="poy-lottery-option-name">{{ $tDisplay(option.name) }}</span>
                  </span>
                </template>
                <template #selected-option="option">
                  <span class="poy-lottery-option">
                    <span
                      v-if="!option.isYeekee && hasFlag(option.name)"
                      class="flag-icon flag-icon-rounded poy-lottery-icon"
                      :class="getFlagIconClass(option.name)"
                      aria-hidden="true"
                    />
                    <span class="poy-lottery-option-name">{{ $tDisplay(option.name) }}</span>
                  </span>
                </template>
              </v-select>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
          >
            <b-form-group
              :label="$t('Status')"
              label-for="poy-status"
            >
              <v-select
                v-model="form.status"
                input-id="poy-status"
                :options="statusOptions"
                label="text"
                :get-option-label="option => $tDisplay(option.text)"
                :reduce="option => option.value"
                :clearable="form.status !== ''"
                :placeholder="$t('Status')"
                @input="changeStatus"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="6"
            lg="3"
            class="d-none d-lg-flex align-items-end"
          >
            <b-button
              type="submit"
              variant="primary"
              class="poy-search w-100 mb-1"
              :disabled="loading"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-50"
              /><feather-icon
                v-else
                icon="SearchIcon"
                size="16"
                class="mr-50"
              />{{ $t('Search') }}
            </b-button>
          </b-col>
        </b-row>
        <b-collapse
          id="poy-advanced-filters"
          :visible="advanced"
        >
          <b-row>
            <b-col
              cols="12"
              md="6"
              lg="3"
            >
              <b-form-group
                :label="$t('ประเภทการเเทง')"
                label-for="poy-bet-type"
                :description="!form.type ? $t('poyReport.selectLotteryFirst') : null"
              >
                <v-select
                  v-model="form.betType"
                  input-id="poy-bet-type"
                  :options="betTypes"
                  label="text"
                  :get-option-label="option => $tDisplay(option.text)"
                  :reduce="option => option.value"
                  :clearable="form.betType !== ''"
                  :disabled="!form.type"
                  :placeholder="$t('ประเภทการเเทง')"
                  @input="changeBetType"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
              lg="3"
            >
              <b-form-group
                :label="$t('เลข')"
                label-for="poy-number"
              >
                <b-form-input
                  id="poy-number"
                  v-model.trim="form.number"
                  inputmode="numeric"
                  maxlength="6"
                  :placeholder="$t('เลข')"
                  autocomplete="off"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-collapse>
        <div class="poy-filter-footer">
          <div class="poy-filter-tools">
            <b-button
              v-for="preset in presets"
              :key="preset.key"
              variant="outline-secondary"
              size="sm"
              @click="setDates(preset.days)"
            >
              {{ $t(preset.label) }}
            </b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              :aria-expanded="advanced ? 'true' : 'false'"
              aria-controls="poy-advanced-filters"
              @click="advanced = !advanced"
            >
              <feather-icon
                icon="SlidersIcon"
                size="14"
                class="mr-25"
              />{{ $t('poyReport.moreFilters') }}
              <b-badge
                v-if="advancedFilterCount"
                variant="light-secondary"
                class="poy-item-count ml-50"
                :aria-label="$t('poyReport.additionalFilterCount', { count: advancedFilterCount })"
              >{{ advancedFilterCount }}</b-badge>
            </b-button>
          </div>
        </div>
        <small class="poy-muted d-block mt-50">{{ $t('poyReport.searchHint') }}</small>
        <b-button
          type="submit"
          variant="primary"
          class="poy-search d-lg-none w-100 mt-1"
          :disabled="loading"
        >
          <b-spinner
            v-if="loading"
            small
            class="mr-50"
          /><feather-icon
            v-else
            icon="SearchIcon"
            size="16"
            class="mr-50"
          />{{ $t('Search') }}
        </b-button>
        <b-alert
          :show="!!validationError"
          variant="warning"
          class="poy-validation-alert mt-1 mb-0"
        >
          <div class="p-1">
            {{ validationError }}
          </div>
        </b-alert>
      </b-form>
      <b-alert
        :show="dictionaryError"
        variant="warning"
        class="mt-1 mb-0"
      >
        <div class="d-flex justify-content-between align-items-center p-1">
          <span>{{ $t('poyReport.lotteryError') }}</span><b-button
            size="sm"
            variant="outline-warning"
            @click="loadDictionaries"
          >
            {{ $t('poyReport.retry') }}
          </b-button>
        </div>
      </b-alert>
    </b-card>

    <b-alert
      :show="!!error"
      variant="danger"
    >
      <div class="d-flex align-items-center justify-content-between p-1">
        <span>{{ error }}</span><b-button
          variant="outline-danger"
          size="sm"
          @click="loadReport"
        >
          {{ $t('poyReport.retry') }}
        </b-button>
      </div>
    </b-alert>

    <b-card
      ref="results"
      class="poy-results"
      :style="loading && resultsHeight ? { minHeight: `${resultsHeight}px` } : {}"
      :aria-busy="loading ? 'true' : 'false'"
    >
      <div
        class="poy-metrics"
        aria-live="polite"
      >
        <div
          v-for="metric in metrics"
          :key="metric.key"
          class="poy-metric"
        >
          <span class="poy-muted">{{ $t(metric.label) }}</span>
          <strong :class="metric.primary ? 'text-primary' : ''">{{ loading ? '—' : metric.value }}</strong>
        </div>
      </div>
      <div class="poy-table-toolbar">
        <div>
          <h5 class="mb-25">
            {{ $t('poyReport.title') }}
          </h5>
          <small class="poy-muted">{{ applied.agent || $t('poyReport.ownScope') }} · {{ displayDate(applied.startDate) }} – {{ displayDate(applied.endDate) }}</small>
        </div>
        <div class="d-flex align-items-center">
          <b-button
            v-if="applied.rowId"
            size="sm"
            variant="flat-primary"
            class="mr-50"
            @click="clearLinkedBill"
          >
            {{ $t('poyReport.allPoys') }}
          </b-button>
          <b-button
            size="sm"
            variant="flat-secondary"
            :disabled="!expandedCount"
            @click="collapseAll"
          >
            <feather-icon
              icon="ChevronsUpIcon"
              size="14"
              class="mr-25"
            />{{ $t('poyReport.collapseAll') }}
          </b-button>
        </div>
      </div>
      <b-table
        :items="rows"
        :fields="fields"
        :busy="loading"
        :tbody-tr-class="rowClass"
        :tbody-tr-attr="rowAttrs"
        primary-key="txId"
        responsive
        show-empty
        class="poy-table report-table-colors report-table-header mb-0"
        @row-clicked="onRowClick"
      >
        <template #table-busy>
          <div
            class="poy-loading"
            role="status"
          >
            <b-spinner
              variant="primary"
              class="mb-1"
            /><span>{{ $t('กำลังโหลดข้อมูล...') }}</span>
          </div>
        </template>
        <template #empty>
          <div class="poy-empty">
            <feather-icon
              icon="FileTextIcon"
              size="32"
              class="poy-muted mb-1"
            /><h5>{{ $t(error ? 'poyReport.unavailable' : 'poyReport.empty') }}</h5><span class="poy-muted">{{ $t('poyReport.emptyHint') }}</span>
          </div>
        </template>
        <template #cell(betId)="{ item }">
          <b-button
            variant="link"
            class="poy-id p-0"
            :aria-expanded="isExpanded(item) ? 'true' : 'false'"
            :aria-controls="`poy-detail-${item.txId}`"
            @click.stop="onPoyClick(item, $event)"
          >
            #{{ item.betId || '—' }}
          </b-button>
          <small class="poy-muted d-block mt-25 text-nowrap">{{ reportDateTime(item.betTime) }}</small>
        </template>
        <template #cell(username)="{ item }">
          <strong>{{ item.username }}</strong><small class="poy-muted d-block mt-25">{{ item.agent }}<template v-if="item.prefix"> · {{ item.prefix }}</template></small>
        </template>
        <template #cell(huayName)="{ item }">
          <div class="poy-lottery">
            <span class="poy-lottery-name">
              <span
                v-if="hasFlag(item.huayName)"
                class="flag-icon flag-icon-rounded poy-lottery-icon"
                :class="getFlagIconClass(item.huayName)"
                aria-hidden="true"
              />
              <span>{{ item.huayName }}</span>
            </span>
            <small class="poy-muted">{{ $t('Round ID') }} {{ item.roundId }}</small>
          </div>
        </template>
        <template #cell(itemCount)="{ value }">
          <b-badge
            variant="light-secondary"
            class="poy-item-count"
          >
            {{ value }}
          </b-badge>
        </template>
        <template #cell(betAmount)="{ value }">
          <strong>{{ money(value) }}</strong>
        </template>
        <template #cell(cashBackAmount)="{ value }">
          {{ money(value) }}
        </template>
        <template #cell(winAmount)="{ value }">
          <strong
            v-if="value > 0"
            class="poy-win-amount"
          >{{ money(value) }}</strong>
          <span v-else>{{ money(value) }}</span>
        </template>
        <template #cell(settlementBet)="{ value }">
          <strong class="text-primary">{{ money(value) }}</strong>
        </template>
        <template #cell(status)="{ value }">
          <b-badge
            :variant="statusVariant(value)"
            :class="{ 'report-status-win': value === 1, 'report-status-loss': value === 2 }"
          >
            {{ $t(statusKey(value)) }}
          </b-badge>
        </template>
        <template #cell(actions)="{ item }">
          <b-button
            variant="flat-primary"
            size="sm"
            class="poy-expand"
            :aria-expanded="isExpanded(item) ? 'true' : 'false'"
            :aria-controls="`poy-detail-${item.txId}`"
            :aria-label="$t(isExpanded(item) ? 'poyReport.collapse' : 'poyReport.details')"
            @click.stop="onPoyClick(item, $event)"
          >
            <feather-icon
              icon="ChevronDownIcon"
              size="18"
              :class="{ 'poy-chevron-open': isExpanded(item) }"
            />
          </b-button>
        </template>
        <template #row-details="{ item }">
          <poy-details
            :tx-id="item.txId"
            :expanded="isExpanded(item)"
            :state="details[item.txId]"
            :query="applied"
            :lotteries="lotteries"
            :scoped="!!applied.agent"
            :link="billLink(item)"
            @retry="loadDetail(item, true)"
            @collapse="togglePoy(item)"
            @collapsed="finishCollapse(item)"
          />
        </template>
      </b-table>
      <div class="report-pagination">
        <div class="report-page-size">
          <label for="poy-limit">{{ $t('reportPagination.perPage') }}</label>
          <b-form-select
            id="poy-limit"
            :value="applied.limit"
            :options="[10, 25, 50, 100]"
            size="sm"
            :disabled="loading"
            @change="changeLimit"
          />
        </div>
        <b-pagination
          :value="applied.page"
          :total-rows="count"
          :per-page="applied.limit"
          :disabled="loading"
          class="mb-0"
          :aria-label="$t('reportPagination.navigation')"
          @change="changePage"
        />
        <small class="report-pagination-summary">{{ $t('poyReport.showingPoys', { start: count ? (applied.page - 1) * applied.limit + 1 : 0, end: Math.min(applied.page * applied.limit, count), total: count }) }}</small>
      </div>
    </b-card>
  </section>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import {
  BAlert, BBadge, BButton, BCard, BCollapse, BCol, BForm, BFormGroup, BFormInput, BFormSelect, BPagination, BRow, BSpinner, BTable,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import { countryFlagMixin } from '@core/mixins/ui/countryFlag'
import reportService from '@/service/reportService'
import settlementService from '@/service/settlementService'
import lottoService from '@/service/lottoService'
import createPoyExpansionState from '@/utils/poyExpansionState'
import { fourFiveDigitAccessOf } from '@/utils/betTypes'
import {
  betTypeLabel, betTypeLabels, money, poyDateLabel, poyDatePreset, queryText, readPoyQuery, reportDateTime, reportQueryString, statusKey, statusVariant, validRange,
} from '@/utils/poyReport'
import PoyDetails from './PoyDetails.vue'

export default {
  components: {
    BAlert, BBadge, BButton, BCard, BCollapse, BCol, BForm, BFormGroup, BFormInput, BFormSelect, BPagination, BRow, BSpinner, BTable, flatPickr, vSelect, AgentSelector, PoyDetails,
  },
  mixins: [countryFlagMixin],
  data() {
    return {
      form: readPoyQuery(this.$route.query),
      applied: readPoyQuery(this.$route.query),
      selectedAgent: null,
      rows: [],
      details: {},
      expansionState: null,
      count: 0,
      totals: null,
      loading: false,
      error: '',
      validationError: '',
      reportRequestId: 0,
      dictionaryRequestId: 0,
      agentRequestId: 0,
      resultsHeight: 0,
      canSelectAgent: false,
      advanced: false,
      // 4/5 ตัว: { [huayId]: ประเภทที่ใช้ได้ } สำหรับตัวกรองประเภท
      fourFiveDigitAccess: {},
      lotteries: [],
      dictionariesLoading: false,
      dictionaryError: false,
      dateConfig: {
        dateFormat: 'Y-m-d H:i', altInput: true, altFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true, minuteIncrement: 1, disableMobile: true, allowInput: true, onReady: this.labelDateInput,
      },
      presets: [{ key: 'today', days: 1, label: 'dateRange.today' }, { key: 'week', days: 7, label: 'poyReport.lastWeek' }, { key: 'month', days: 30, label: 'poyReport.lastMonth' }],
    }
  },
  computed: {
    reportUserKey() {
      const user = this.$store.state.app.userData
      return user?.username ? JSON.stringify([user.id || '', user.agent || '', user.username, user.role || '']) : ''
    },
    expansionScope() { return this.reportUserKey ? JSON.stringify([this.reportUserKey, this.applied]) : '' },
    showAgent() { return !!this.applied.agent || this.canSelectAgent },
    summaryQuery() {
      const source = queryText(this.$route.query, 'summary')
      if (!source) return null
      const params = new URLSearchParams(source)
      return Object.fromEntries(['agent', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'].filter(key => params.has(key)).map(key => [key, params.get(key)]))
    },
    expandedCount() { return this.rows.filter(this.isExpanded).length },
    advancedFilterCount() { return Number(!!this.form.number) + Number(!!this.form.betType) },
    lotteryOptions() { return [{ id: '', name: 'ทั้งหมด' }, ...this.lotteries] },
    statusOptions() { return [{ value: '', text: 'ทั้งหมด' }, ...[0, 1, 2, -1].map(value => ({ value: String(value), text: statusKey(value) }))] },
    betTypes() {
      const lottery = this.lotteries.find(v => String(v.id) === this.form.type)
      // 4/5 ตัว: ต่อท้ายเฉพาะประเภทที่เปิดใช้จริงในหวยที่เลือก (ไม่เลือกหวย = หวยใดหวยหนึ่ง) · ห้องยี่กีใช้ชุดของยี่กี
      // ตัวกรองที่มากับ URL ยังอยู่ในรายการแม้ปิดไปแล้ว เพื่อค้นโพยเก่าต่อได้
      const lists = this.form.type ? [fourFiveDigitAccessOf(this.fourFiveDigitAccess, this.form.type)] : Object.values(this.fourFiveDigitAccess)
      const restored = Number(this.form.betType) > 40 ? [Number(this.form.betType)] : []
      const fourFiveDigit = [...new Set([].concat(...lists, restored))].sort((a, b) => a - b)
        .map(type => ({ value: String(type), text: betTypeLabel(this.form.type, type) }))
      return [{ value: '', text: 'ทั้งหมด' }, ...betTypeLabels(this.form.type, lottery?.type).map((label, index) => ({ value: String(index + 1), text: label })), ...fourFiveDigit]
    },
    fields() {
      const result = [
        ['betId', 'poyReport.poyId'], ['username', 'Username'], ['huayName', 'Huay Name'], ['itemCount', 'poyReport.itemCount'],
        ['betAmount', 'Bet Amount'], ['cashBackAmount', 'poyReport.cashback'], ['winAmount', 'poyReport.payout'],
      ]
      if (this.applied.agent) result.push(['settlementBet', 'downlineReports.settlementBet'])
      result.push(['status', 'Status'], ['actions', 'poyReport.details'])
      return result.map(([key, label]) => ({
        key, label: this.$t(label), class: ['betAmount', 'cashBackAmount', 'winAmount', 'settlementBet'].includes(key) ? 'text-right text-nowrap' : 'text-nowrap', thClass: key === 'actions' ? 'text-center' : '', tdClass: key === 'actions' ? 'text-center' : '',
      }))
    },
    metrics() {
      const totals = this.totals || {}
      return [
        { key: 'count', label: 'poyReport.count', value: (totals.count || 0).toLocaleString('en-US') },
        { key: 'bet', label: 'poyReport.totalBet', value: money(totals.betAmount) },
        this.applied.agent
          ? {
            key: 'settlement', label: 'downlineReports.settlementBet', value: money(totals.settlementBet), primary: true,
          }
          : { key: 'cashback', label: 'poyReport.cashback', value: money(totals.cashBackAmount) },
        { key: 'win', label: 'poyReport.payout', value: money(totals.winAmount) },
      ]
    },
  },
  watch: {
    '$route.query': 'restoreRoute',
    reportUserKey() {
      this.expansionState.clear()
      this.restoreRoute()
    },
  },
  created() {
    this.expansionState = createPoyExpansionState()
    this.restoreRoute()
    this.loadDictionaries()
    this.loadFourFiveDigitAccess()
    this.loadAgentAccess()
  },
  beforeDestroy() {
    // SPA navigation starts a fresh visit; browser reload does not run this hook.
    this.expansionState.clear()
    this.reportRequestId += 1
    this.dictionaryRequestId += 1
    this.agentRequestId += 1
  },
  methods: {
    money,
    reportDateTime,
    statusKey,
    statusVariant,
    labelDateInput(dates, value, picker) {
      const { altInput, input } = picker
      if (altInput) altInput.id = `${input.id}-display`
    },
    displayDate: poyDateLabel,
    changeLottery(value) {
      this.form.type = value || ''
      this.form.betType = ''
    },
    changeStatus(value) {
      this.form.status = value ?? ''
    },
    changeBetType(value) {
      this.form.betType = value ?? ''
    },
    restoreRoute() {
      this.applied = readPoyQuery(this.$route.query)
      this.form = { ...this.applied }
      this.selectedAgent = this.applied.agent ? { code: this.applied.agent, username: this.applied.agent, label: this.applied.agent } : null
      this.advanced = !!(this.applied.number || this.applied.betType)
      this.validationError = ''
      this.loadReport()
    },
    navigate(params) {
      const query = Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '' && value !== null).map(([key, value]) => [key, String(value)]))
      if (this.$route.query.summary) query.summary = this.$route.query.summary
      const destination = { name: 'poy-report', query }
      if (this.$router.resolve(destination).route.fullPath === this.$route.fullPath) this.loadReport()
      else this.$router.push(destination)
    },
    search() {
      if (!validRange(this.form.startDate, this.form.endDate)) {
        this.validationError = this.$t('poyReport.dateError')
        return
      }
      this.validationError = ''
      this.navigate({
        ...this.form, rowId: '', agent: this.selectedAgent?.code || '', page: 1,
      })
    },
    setDates(days) {
      Object.assign(this.form, poyDatePreset(days))
    },
    changePage(page) { this.navigate({ ...this.applied, page }) },
    changeLimit(limit) { this.navigate({ ...this.applied, limit, page: 1 }) },
    clearLinkedBill() { this.navigate({ ...this.applied, rowId: '', page: 1 }) },
    errorMessage(status) {
      if (status === 400) return this.$t('poyReport.narrowSearch')
      if (status === 404) return this.$t('poyReport.notFound')
      return this.$t('downlineReports.loadError')
    },
    async loadReport() {
      this.reportRequestId += 1
      const requestId = this.reportRequestId
      const scope = this.expansionScope
      const expandedIds = this.expansionState.read(scope)
      this.resultsHeight = this.$refs.results?.$el?.getBoundingClientRect().height || 0
      this.loading = true
      this.error = ''
      this.details = {}
      this.rows = []
      this.count = 0
      this.totals = null
      if (!this.reportUserKey) { this.loading = false; return }
      if (!validRange(this.applied.startDate, this.applied.endDate)) {
        this.error = this.$t('poyReport.dateError')
        this.loading = false
        return
      }
      try {
        const res = await reportService.getAuthen(`poy?${reportQueryString(this.applied)}`)
        if (requestId !== this.reportRequestId || scope !== this.expansionScope) return
        if (res.status !== 200) {
          if (res.status === 401 || res.status === 403) this.expansionState.clear()
          this.error = this.errorMessage(res.status)
          return
        }
        this.rows = res.data.list.map(row => ({ ...row, _showDetails: false }))
        this.count = res.data.count
        this.totals = Object.freeze(res.data.totals)
        const ids = expandedIds === null && this.applied.rowId && this.rows.length === 1
          ? [this.rows[0].txId] : expandedIds || []
        this.rows.filter(row => ids.includes(row.txId)).forEach(row => this.togglePoy(row, false))
        this.rememberExpandedPoys()
      } catch (error) {
        if (requestId === this.reportRequestId && scope === this.expansionScope) this.error = this.errorMessage(0)
      } finally {
        if (requestId === this.reportRequestId && scope === this.expansionScope) this.loading = false
      }
    },
    isExpanded(row) { return !!this.details[row.txId]?.expanded },
    onRowClick(row, index, event) { this.onPoyClick(row, event) },
    onPoyClick(row, event) {
      if (this.loading) return
      // BTable protects row clicks during selection. Apply the same protection
      // to the explicit buttons, including dragging over the Poy ID to copy it.
      if (event?.type === 'click' && event.detail !== 0) {
        const element = event.target?.closest?.('tr')
        const selection = event.target?.ownerDocument?.getSelection?.()
        if (element && selection?.toString().trim() && selection.containsNode?.(element, true)) return
      }
      this.togglePoy(row)
    },
    rememberExpandedPoys() {
      this.expansionState.write(this.expansionScope, this.rows.filter(this.isExpanded).map(row => row.txId))
    },
    togglePoy(row, remember = true) {
      if (!this.details[row.txId]) {
        this.$set(this.details, row.txId, {
          expanded: false, loading: false, error: '', data: null,
        })
      }
      const state = this.details[row.txId]
      state.expanded = !state.expanded
      if (state.expanded) {
        this.$set(row, '_showDetails', true)
        this.loadDetail(row)
      }
      if (remember) this.rememberExpandedPoys()
    },
    finishCollapse(row) {
      if (!this.isExpanded(row)) this.$set(row, '_showDetails', false)
    },
    collapseAll() {
      Object.values(this.details).forEach(state => { state.expanded = false })
      this.rememberExpandedPoys()
    },
    rowClass(row, type) {
      if (!row || type !== 'row') return ''
      return this.isExpanded(row) ? 'poy-row poy-row-open' : 'poy-row'
    },
    rowAttrs(row, type) {
      if (!row || type !== 'row') return {}
      return { 'aria-expanded': String(this.isExpanded(row)), 'aria-controls': `poy-detail-${row.txId}` }
    },
    async loadDetail(row, force = false) {
      const state = this.details[row.txId]
      if (!state || state.loading || (state.data && !force)) return
      const requestId = this.reportRequestId
      const scope = this.expansionScope
      state.loading = true
      state.error = ''
      try {
        const params = { agent: this.applied.agent, startDate: this.applied.startDate, endDate: this.applied.endDate }
        const res = await reportService.getAuthen(`poy/by-row/${encodeURIComponent(row.txId)}?${reportQueryString(params)}`)
        if (requestId !== this.reportRequestId || scope !== this.expansionScope) return
        if (res.status === 403 || res.status === 401) {
          this.expansionState.clear()
          this.rows = []
          this.details = {}
          this.totals = null
          this.count = 0
          this.error = this.errorMessage(res.status)
          this.reportRequestId += 1
          return
        }
        if (res.status !== 200) { state.data = null; state.error = this.errorMessage(res.status); return }
        state.data = Object.freeze({ bill: Object.freeze(res.data.bill), items: Object.freeze(res.data.items) })
      } catch (error) {
        if (requestId === this.reportRequestId && scope === this.expansionScope) state.error = this.errorMessage(0)
      } finally {
        if (requestId === this.reportRequestId && scope === this.expansionScope) state.loading = false
      }
    },
    billLink(row) {
      const query = {
        agent: this.applied.agent, startDate: this.applied.startDate, endDate: this.applied.endDate, rowId: row.txId,
      }
      return this.$router.resolve({ name: 'poy-report', query }).href
    },
    async loadAgentAccess() {
      this.agentRequestId += 1
      const requestId = this.agentRequestId
      try {
        const res = await settlementService.getAuthen('settlement/bets/agents?page=1&limit=1')
        if (requestId === this.agentRequestId) this.canSelectAgent = res.status === 200 && res.data.total > 0
      } catch (error) {
        if (requestId === this.agentRequestId) this.canSelectAgent = false
      }
    },
    // อ่านผ่าน lottoService ตรง ๆ — ไม่มีสิทธิ์หรืออ่านไม่ได้ = ไม่มีตัวกรองประเภทใหม่
    // inUse: master ได้เฉพาะที่เปิดใช้จริงตอนนี้ ปิดฟีเจอร์แล้วตัวกรองเหมือนเดิม
    async loadFourFiveDigitAccess() {
      try {
        const res = await lottoService.getAuthen('four-five-digit/access?inUse=true')
        this.fourFiveDigitAccess = res && res.status === 200 && res.data && !Array.isArray(res.data) ? res.data : {}
      } catch {
        this.fourFiveDigitAccess = {}
      }
    },
    async loadDictionaries() {
      this.dictionaryRequestId += 1
      const requestId = this.dictionaryRequestId
      this.dictionariesLoading = true
      this.dictionaryError = false
      try {
        const results = await Promise.all([lottoService.getAuthen('huay/dict'), lottoService.getAuthen('yeekee/dict')])
        if (requestId !== this.dictionaryRequestId) return
        if (results.some(res => res.status !== 200 || !Array.isArray(res.data))) throw new Error('Dictionary unavailable')
        this.lotteries = Object.freeze(results.flatMap((res, index) => res.data.map(lottery => ({
          ...lottery, isYeekee: index === 1,
        }))))
      } catch (error) {
        if (requestId === this.dictionaryRequestId) this.dictionaryError = true
      } finally {
        if (requestId === this.dictionaryRequestId) this.dictionariesLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.poy-report {
  .poy-heading, .poy-filter-footer, .poy-table-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .poy-heading-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    color: var(--report-accent);
    background: var(--report-heading-icon-bg);
  }
  .poy-search { min-width: 120px; }
  .poy-validation-alert { color: var(--report-warning-text) !important; }
  .poy-filter-tools { display: flex; flex-wrap: wrap; align-items: center; gap: 0.5rem; }
  .poy-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); padding: 0 0 1.4rem; border-bottom: 1px solid var(--report-border); }
  .poy-metric { padding: 0 1.5rem; display: flex; flex-direction: column; gap: 0.4rem; }
  .poy-metric:first-child { padding-left: 0; }
  .poy-metric:last-child { padding-right: 0; }
  .poy-metric + .poy-metric { border-left: 1px solid var(--report-border); }
  .poy-metric strong { font-size: 1.5rem; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .poy-table-toolbar { padding: 1.25rem 0; }
  .poy-table { border: 1px solid var(--report-border); border-radius: 0.5rem; }
  .poy-table td { vertical-align: middle; padding: 1rem; font-variant-numeric: tabular-nums; transition: background-color 160ms ease, box-shadow 160ms ease; }
  .poy-lottery { display: grid; align-items: center; row-gap: 0.25rem; }
  .poy-lottery-name { display: inline-flex; align-items: center; gap: 0.625rem; }
  .poy-lottery-icon { width: 18px !important; height: 18px !important; border: 0; box-shadow: var(--report-flag-shadow); }
  .poy-lottery-option { display: inline-flex; align-items: center; gap: .625rem; min-width: 0; max-width: 100%; }
  .poy-lottery-option-name { min-width: 0; white-space: normal; }
  .poy-lottery-select .vs__selected-options { flex-wrap: nowrap; min-width: 0; }
  .poy-lottery-select .vs__selected { min-width: 0; max-width: 100%; }
  .poy-lottery-select .vs__selected .poy-lottery-option-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .poy-id { font-weight: 600; text-align: left; color: var(--report-accent); }
  .poy-muted { color: var(--report-body-text); }
  .poy-win-amount { color: var(--report-success-text); }
  .poy-table .poy-row { cursor: pointer; }
  .poy-table .poy-row > td:first-child { box-shadow: inset 3px 0 0 transparent; }
  .poy-table .poy-row-open > td { background-color: var(--report-row-selected-bg); }
  .poy-table .poy-row-open > td:first-child { box-shadow: inset 3px 0 0 var(--hdg-brand-primary, #b5122b); }
  .poy-table .poy-row:focus-visible { outline: 2px solid var(--hdg-brand-primary, #b5122b); outline-offset: -2px; }
  .poy-table .poy-row:focus-visible > td { background-color: var(--report-row-hover-bg); }
  .poy-table .poy-row-open:focus-visible > td { background-color: var(--report-row-selected-hover-bg); }
  .poy-table .b-table-details { cursor: auto; }
  .poy-table .b-table-details > td { padding: 0; border-top: 0; }
  .poy-expand {
    --poy-expand-rest-bg: transparent;
    transition: background-color 160ms ease, color 160ms ease;
  }
  .poy-row-open .poy-expand,
  .poy-row:focus-visible .poy-expand {
    --poy-expand-rest-bg: var(--report-expand-bg);
    background-color: var(--poy-expand-rest-bg);
    color: var(--report-accent);
  }
  .poy-expand:focus-visible {
    background-color: var(--report-expand-bg);
    outline: 2px solid var(--hdg-brand-primary-emphasis, #b5122b);
    outline-offset: 2px;
  }
  // Pointer clicks retain DOM focus. Restore the row's fill when hover ends,
  // overriding the shared theme's focus fill without removing keyboard focus.
  .poy-expand.btn-flat-primary:focus:not(:focus-visible):not(:hover):not(:active):not(.active) {
    background-color: var(--poy-expand-rest-bg) !important;
  }
  .poy-expand svg { transition: transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  .poy-chevron-open { transform: rotate(180deg); }
  .poy-loading, .poy-empty { min-height: 260px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2rem; }
  .collapsing { transition: height 220ms cubic-bezier(0.2, 0.8, 0.2, 1); }
  @media (hover: hover) {
    .poy-table .poy-row:hover > td { background-color: var(--report-row-hover-bg); }
    .poy-table .poy-row-open:hover > td { background-color: var(--report-row-selected-hover-bg); }
    .poy-row:hover .poy-expand {
      --poy-expand-rest-bg: var(--report-expand-bg);
      background-color: var(--poy-expand-rest-bg);
      color: var(--report-accent);
    }
  }
  @media (max-width: 767px) {
    .poy-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1.5rem 0; }
    .poy-metric:nth-child(3) { padding-left: 0; border-left: 0; }
    .poy-metric strong { font-size: 1.2rem; }
    .poy-search { width: 100%; }
  }
  @media (prefers-reduced-motion: reduce) {
    .collapsing, .poy-expand, .poy-expand svg, .poy-table td { transition: none; }
  }
}

.dark-layout .poy-report {
  .poy-filter-card .form-text.text-muted { color: var(--report-secondary-text) !important; }
  .poy-table .poy-row .poy-muted { color: var(--report-secondary-text); }
  .poy-expand.btn-flat-primary:not(:disabled):not(.disabled) {
    // The shared dark button theme marks its interaction colors !important.
    color: var(--report-action-text) !important;
    &:hover,
    &:focus-visible {
      background-color: var(--report-expand-hover-bg) !important;
    }
    &:active,
    &.active {
      background-color: var(--report-expand-active-bg) !important;
    }
    &:focus-visible {
      outline: 2px solid var(--report-action-text);
      outline-offset: 2px;
    }
  }
  .badge.poy-item-count {
    color: var(--report-count-text) !important;
    background-color: var(--report-count-bg);
  }
  // Override the dark table rule that clears cells on b-table-has-details rows.
  .poy-table .poy-row-open > td { background-color: var(--report-row-selected-bg); }
  .poy-table .poy-row-open:focus-visible > td { background-color: var(--report-row-selected-hover-bg); }
  @media (hover: hover) {
    .poy-table .poy-row-open:hover > td { background-color: var(--report-row-selected-hover-bg); }
  }
}
</style>
